import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import { apiService } from '../services/api';
import { FiSliders, FiSave, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiStar } from 'react-icons/fi';

interface WeightTemplateData {
  id?: number;
  name_en: string;
  name_fr: string;
  description_en: string;
  description_fr: string;
  experience_weight: number;
  skills_weight: number;
  education_weight: number;
  is_default: boolean;
  display_order: number;
}

interface AppSettingsData {
  id: number;
  default_language_percent: number;
  updated_at: string;
  [key: string]: any;
}

const EMPTY_TEMPLATE: WeightTemplateData = {
  name_en: '',
  name_fr: '',
  description_en: '',
  description_fr: '',
  experience_weight: 33,
  skills_weight: 34,
  education_weight: 33,
  is_default: false,
  display_order: 0,
};

const WeightTemplates: React.FC = () => {
  const [settings, setSettings] = useState<AppSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  // Weight Templates state
  const [weightTemplates, setWeightTemplates] = useState<WeightTemplateData[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<WeightTemplateData | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    fetchSettings();
    fetchWeightTemplates();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAppSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchWeightTemplates = async () => {
    try {
      const response = await apiService.getWeightTemplates();
      if (response.success && response.data) {
        setWeightTemplates(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch weight templates:', error);
    }
  };

  const handleSaveLanguagePercent = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const response = await apiService.updateAppSettings({ default_language_percent: settings.default_language_percent });
      if (response.success) {
        setToast({ message: 'Language weight saved successfully', type: 'success' });
      } else {
        setToast({ message: 'Failed to save language weight', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to save language weight', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!editingTemplate) return;
    setTemplateSaving(true);
    try {
      const response = await apiService.createWeightTemplate(editingTemplate);
      if (response.success) {
        setToast({ message: 'Weight template created successfully', type: 'success' });
        setEditingTemplate(null);
        setIsCreating(false);
        fetchWeightTemplates();
      } else {
        const err = response as any;
        setToast({ message: err.error?.message || 'Failed to create template', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to create weight template', type: 'error' });
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !editingTemplate.id) return;
    setTemplateSaving(true);
    try {
      const response = await apiService.updateWeightTemplate(editingTemplate.id, editingTemplate);
      if (response.success) {
        setToast({ message: 'Weight template updated successfully', type: 'success' });
        setEditingTemplate(null);
        fetchWeightTemplates();
      } else {
        const err = response as any;
        setToast({ message: err.error?.message || 'Failed to update template', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update weight template', type: 'error' });
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this weight template?')) return;
    setDeletingId(id);
    try {
      const response = await apiService.deleteWeightTemplate(id);
      if (response.success) {
        setToast({ message: 'Weight template deleted successfully', type: 'success' });
        fetchWeightTemplates();
      } else {
        setToast({ message: 'Failed to delete weight template', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to delete weight template', type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Weight Templates</h1>
          </div>
          <button
            onClick={handleSaveLanguagePercent}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            <FiSave className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Language Weight'}
          </button>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="space-y-6">
            {/* Default Language Percent */}
            {settings && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 dark:border-indigo-800 dark:bg-indigo-900/20">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">🌐 Default Language Weight</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={settings.default_language_percent}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d+$/.test(v)) {
                          setSettings({ ...settings, default_language_percent: v === '' ? 0 : parseInt(v) });
                        }
                      }}
                      className="w-20 rounded-lg border border-indigo-300 px-3 py-2 text-center text-sm font-semibold dark:border-indigo-600 dark:bg-navy-700 dark:text-white"
                    />
                    <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Weight Templates Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Weight Templates</h3>
              <button
                onClick={() => {
                  setEditingTemplate({ ...EMPTY_TEMPLATE, display_order: weightTemplates.length + 1 });
                  setIsCreating(true);
                }}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition"
              >
                <FiPlus className="h-4 w-4" />
                Add Template
              </button>
            </div>

            {/* Create / Edit Form */}
            {editingTemplate && (
              <div className="rounded-xl border-2 border-brand-300 bg-brand-50 p-5 dark:border-brand-700 dark:bg-brand-900/20">
                <h4 className="mb-4 text-sm font-semibold text-brand-700 dark:text-brand-300">
                  {isCreating ? 'Create New Template' : `Edit: ${editingTemplate.name_en}`}
                </h4>
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Name (English)</label>
                      <input
                        type="text"
                        value={editingTemplate.name_en}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, name_en: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                        placeholder="e.g. Senior"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Name (French)</label>
                      <input
                        type="text"
                        value={editingTemplate.name_fr}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, name_fr: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                        placeholder="e.g. Senior"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Description (English)</label>
                      <textarea
                        value={editingTemplate.description_en}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, description_en: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                        placeholder="Brief description of when to use this template"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Description (French)</label>
                      <textarea
                        value={editingTemplate.description_fr}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, description_fr: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                        placeholder="Brève description de l'utilisation de ce modèle"
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-green-600 dark:text-green-400">Experience %</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editingTemplate.experience_weight}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d+$/.test(v)) {
                            setEditingTemplate({ ...editingTemplate, experience_weight: v === '' ? 0 : parseInt(v) });
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-sm font-semibold dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-purple-600 dark:text-purple-400">Skills %</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editingTemplate.skills_weight}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d+$/.test(v)) {
                            setEditingTemplate({ ...editingTemplate, skills_weight: v === '' ? 0 : parseInt(v) });
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-sm font-semibold dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-orange-600 dark:text-orange-400">Education %</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editingTemplate.education_weight}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d+$/.test(v)) {
                            setEditingTemplate({ ...editingTemplate, education_weight: v === '' ? 0 : parseInt(v) });
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-sm font-semibold dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Total validation */}
                  {(() => {
                    const total = editingTemplate.experience_weight + editingTemplate.skills_weight + editingTemplate.education_weight;
                    const isValid = total === 100;
                    return (
                      <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${isValid ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                        <span className={`text-sm font-medium ${isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                          Total: {total}%
                        </span>
                        {!isValid && <span className="text-xs text-red-600 dark:text-red-400">Must equal 100%</span>}
                        {isValid && <span className="text-xs text-green-600 dark:text-green-400">✓ Valid</span>}
                      </div>
                    );
                  })()}

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Display Order</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={editingTemplate.display_order}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d+$/.test(v)) {
                            setEditingTemplate({ ...editingTemplate, display_order: v === '' ? 0 : parseInt(v) });
                          }
                        }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 p-2.5 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                        <input
                          type="checkbox"
                          checked={editingTemplate.is_default}
                          onChange={(e) => setEditingTemplate({ ...editingTemplate, is_default: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Default Template</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      onClick={isCreating ? handleCreateTemplate : handleUpdateTemplate}
                      disabled={templateSaving || (editingTemplate.experience_weight + editingTemplate.skills_weight + editingTemplate.education_weight) !== 100}
                      className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition"
                    >
                      <FiCheck className="h-4 w-4" />
                      {templateSaving ? 'Saving...' : isCreating ? 'Create' : 'Update'}
                    </button>
                    <button
                      onClick={() => { setEditingTemplate(null); setIsCreating(false); }}
                      className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-700 transition"
                    >
                      <FiX className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Weight Templates List */}
            <div className="space-y-3">
              {weightTemplates.length === 0 && (
                <div className="rounded-xl border border-gray-200 p-8 text-center dark:border-gray-700">
                  <p className="text-gray-500 dark:text-gray-400">No weight templates found. Click "Add Template" to create one.</p>
                </div>
              )}
              {weightTemplates.map((template) => {
                const total = template.experience_weight + template.skills_weight + template.education_weight;
                const isValid = total === 100;
                return (
                  <div
                    key={template.id}
                    className={`rounded-xl border-2 p-4 ${isValid ? 'border-gray-200 dark:border-gray-700' : 'border-red-300 dark:border-red-700'}`}
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      {/* Badge & Name */}
                      <div className="flex items-center gap-2 min-w-[180px]">

                        <div>
                          <span className="font-semibold text-gray-700 dark:text-gray-300">{template.name_en}</span>
                          <span className="ml-2 text-xs text-gray-400">({template.name_fr})</span>
                          {template.description_en && (
                            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 line-clamp-1">{template.description_en}</p>
                          )}
                        </div>
                        {template.is_default && (
                          <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                            <FiStar className="h-3 w-3" /> Default
                          </span>
                        )}
                      </div>

                      {/* Weights */}
                      <div className="flex items-center gap-3 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-green-600 dark:text-green-400">Exp</span>
                          <span className="rounded bg-green-50 px-2 py-1 text-sm font-bold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {template.experience_weight}%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Skills</span>
                          <span className="rounded bg-purple-50 px-2 py-1 text-sm font-bold text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            {template.skills_weight}%
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-medium text-orange-600 dark:text-orange-400">Edu</span>
                          <span className="rounded bg-orange-50 px-2 py-1 text-sm font-bold text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                            {template.education_weight}%
                          </span>
                        </div>
                      </div>

                      {/* Total */}
                      <div className={`flex items-center gap-1 rounded-lg px-3 py-1.5 ${isValid ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        <span className={`text-sm font-bold ${isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                          {total}%
                        </span>
                        {isValid && <span className="text-green-600">✓</span>}
                        {!isValid && <span className="text-red-600">✗</span>}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditingTemplate({ ...template }); setIsCreating(false); }}
                          className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-navy-700 dark:hover:text-brand-400 transition"
                          title="Edit template"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => template.id && handleDeleteTemplate(template.id)}
                          disabled={deletingId === template.id}
                          className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 disabled:opacity-50 transition"
                          title="Delete template"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default WeightTemplates;
